/* eslint-disable @typescript-eslint/naming-convention */
import type React from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { ALL } from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import useActivationQueryWithAccessControl from "app/custom-hooks/activation/useActivationQueryWithAccessControl";
import { AIAgent } from "app/pages/keyword-research/common-components/aiAgent/AIAgent";
import { AutoCreateKeywordGroupBanner } from "app/pages/keyword-research/keyword-analysis/pages/serp-players/AutoCreateKeywordGroupBanner";
import { useCommonProps } from "app/pages/keyword-research/keyword-analysis/pages/serp-players/useCommonProps";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { createUniqueListOfAllDomains } from "pro-features/components/DomainsFilter/Utils";

import { Graph } from "../../common-components/clicks-over-time-graph/Graph";
import { DomainsTable } from "../../common-components/domains-table/Table";
import { useKeywordAnalysisNavigationTargets } from "app/pages/keyword-research/keyword-analysis/common-components/keywordAnalysisNavigationTargets";

import {
    visibleColumns,
    visibleColumnsEnrich,
    entity,
    TABLE_DASHBOARD_METRIC,
    GRAPH_DASHBOARD_METRIC,
    dropDownColumnsTexts,
} from "app/common/keyword-research/serp-players/consts";
import { FiltersSection } from "./FiltersSection/FiltersSection";
import { useSerpPlayersEndpoints } from "./serpPlayersEndpoints";
import { MetricsRow } from "./styledComponents";
import { noop } from "./types";
import { transformData, transformDataEnrich } from "./utils";

export const SerpPlayers: React.FC = () => {
    const { params } = useCurrentStateAndParams();
    const endpoints = useSerpPlayersEndpoints();
    const navTargets = useKeywordAnalysisNavigationTargets();

    const { category, funcFlag, domains } = params;
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);

    const { data: activationSets } = useActivationQueryWithAccessControl();

    const commonProps = useCommonProps(true);
    const [monthlyVolume] = useState(Infinity);

    return (
        <>
            {!swSettings.components.SEOContentStrategy?.isDisabled ? <AIAgent /> : null}
            <AutoCreateKeywordGroupBanner
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                keyword={params.keyword}
                shouldShowBanner={!commonProps.isKeywordsGroup ? monthlyVolume <= 5000 : null}
            />

            {activationSets ? (
                <>
                    <FiltersSection
                        domainsAPI={endpoints.domains}
                        categoriesAPI={endpoints.categories}
                        activationSets={activationSets}
                    />
                    <MetricsRow>
                        <Graph
                            isKeywordsGroup={commonProps.isKeywordsGroup}
                            filters={{
                                category: category === ALL ? undefined : category,
                                funcFlag,
                                DomainsFilter: createUniqueListOfAllDomains(domains?.split(","), activationSets),
                                sourceType: SourceTypeMap[params.selectedPageTab],
                            }}
                            endpoint={endpoints.graph}
                            dashboardMetric={GRAPH_DASHBOARD_METRIC}
                            excelEndpoint={endpoints.graphExcel}
                        />
                    </MetricsRow>
                    <MetricsRow>
                        <DomainsTable
                            getDataCallback={noop}
                            tableSelectionKey={"keywordAnalysis.mobile.v2.Table"}
                            addToDashboardMetric="KeywordAnalysisTotal"
                            isKeywordsGroup={commonProps.isKeywordsGroup}
                            tableEndpoint={endpoints.table}
                            enrichTableEndpoint={endpoints.enrichTable}
                            visibleColumns={visibleColumns}
                            visibleColumnsEnrich={visibleColumnsEnrich}
                            dropDownColumnsTexts={dropDownColumnsTexts}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            transformData={transformData(
                                isKeywordGroup,
                                params.keyword,
                                navTargets.websiteKeywordsState,
                                params,
                            )}
                            transformDataEnrich={transformDataEnrich}
                            entity={entity}
                            dashboardMetric={TABLE_DASHBOARD_METRIC}
                            activationSets={activationSets}
                            websiteKeywordsState={navTargets.websiteKeywordsState}
                            keywordAdsState={navTargets.keywordAdsState}
                            keywordSerpPlayersState={navTargets.keywordSerpPlayersState}
                        />
                    </MetricsRow>
                </>
            ) : null}
        </>
    );
};
